import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Grid,
    Divider,
    Stack,
    Link
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';

import ExternalApiKeysPanel from './ExternalApiKeysPanel';

// Customer-facing API reference, shipped as a static page from client/public/docs
// so it opens without the SPA and still works once downloaded and opened offline.
export const API_DOCS_URL = `${process.env.PUBLIC_URL}/docs/inscribe-integration-api.html`;
export const API_DOCS_DOWNLOAD_NAME = 'Inscribe-Integration-API-Reference.html';

// The app's global stylesheet forces every <a> to display:block, color:inherit and
// no underline (all !important), so these inline text links have to win it back.
const inlineLinkSx = {
    display: 'inline !important',
    color: (theme) => `${theme.palette.primary.main} !important`,
    '&:hover': { textDecoration: 'underline !important' }
};

const ENDPOINTS = [
    { key: 'endpoint_get_document_enabled', label: 'Get document (GET /api/external/document)' },
    { key: 'endpoint_push_document_enabled', label: 'Push document to archive (POST /api/external/document)' },
    { key: 'endpoint_documents_by_date_enabled', label: 'List documents by date (GET /api/external/documents_by_date)' }
];

function ExternalApiConfigSection({ config, onChange, onSave, hasChanges, localisation }) {
    return (
        <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Integration APIs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A set of REST APIs that let approved third-party applications integrate with the system (pushing and retrieving documents, and other supported operations), authenticated with an application API key and a JWT token.
            </Typography>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5, mb: 3, color: 'text.secondary' }}>
                <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                    <Link href={API_DOCS_URL} target="_blank" rel="noopener noreferrer" underline="hover" sx={inlineLinkSx}>
                        API documentation
                    </Link>
                    {' · '}
                    <Link href={API_DOCS_URL} download={API_DOCS_DOWNLOAD_NAME} underline="hover" sx={inlineLinkSx}>
                        Download
                    </Link>
                </Typography>
            </Stack>

            <FormControlLabel
                control={
                    <Switch
                        checked={config.api_enabled || false}
                        onChange={(e) => onChange('api_enabled', e.target.checked)}
                    />
                }
                label="Enable Integration APIs"
            />

            {config.api_enabled && <ExternalApiKeysPanel />}

            <Divider sx={{ my: 4 }} />

            <Grid container spacing={2}>
                {config.api_enabled && (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Allowed IPs (comma-separated, blank = no restriction)"
                                value={config.allowed_ips || ''}
                                onChange={(e) => onChange('allowed_ips', e.target.value)}
                                placeholder="e.g. 203.0.113.5, 10.0.0.0/24"
                                autoComplete="off"
                                inputProps={{ autoComplete: 'off', 'data-form-type': 'other' }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="JWT Token Expiry (hours)"
                                value={config.token_expiry_hours ?? ''}
                                onChange={(e) => onChange('token_expiry_hours', e.target.value === '' ? null : Number(e.target.value))}
                                placeholder="1"
                                helperText="How long a jwt token  stays valid. 0.1–24 hours, defaults to 1 if blank."
                                inputProps={{ min: 0.1, max: 24, step: 0.1 }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Individual Endpoints
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                The Token endpoint is always enabled, because it's required to obtain a JWT, without which none of the other endpoints can be called.
                            </Typography>
                        </Grid>

                        {ENDPOINTS.map(({ key, label }) => (
                            <Grid item xs={12} key={key}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config[key] !== false}
                                            onChange={(e) => onChange(key, e.target.checked)}
                                        />
                                    }
                                    label={label}
                                />
                            </Grid>
                        ))}
                    </>
                )}
            </Grid>

            <Button
                variant="contained"
                color="primary"
                onClick={onSave}
                disabled={!hasChanges}
                sx={{ mt: 3 }}
            >
                {localisation?.["save_button"]?.element_lang || "Save"}
            </Button>
        </Box>
    );
}

export default ExternalApiConfigSection;
